import React, { useEffect, useState } from "react";
import {
  User,
  Phone,
  Mail,
  MapPin,
  CheckCircle2,
  Calendar,
  Sparkles,
  ArrowRight,
  MessageSquare,
  Building2,
  Home,
  Store,
  Loader2,
} from "lucide-react";
import image1 from "../assets/post.jpg";

export const BookFreeSiteVisitSection = () => {
  const [formData, setFormData] = useState({
    name: "",
    mobile: "",
    pincode: "",
    email: "",
    whatsappUpdates: true,
    userType: "End User (Homeowner/Client)",
    selectedLocation: "",
  });

  const businessPhoneNumber = "+919998852256";
  const [showToast, setShowToast] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isPincodeLoading, setIsPincodeLoading] = useState(false);
  const [pincodeData, setPincodeData] = useState([]);
  const [message, setMessage] = useState("");

  const userTypeOptions = [
    {
      id: "End User (Homeowner/Client)",
      title: "Homeowner / Client",
      desc: "For personal property painting",
      icon: Home,
    },
    {
      id: "Architect/Interior Designer",
      title: "Architect / Designer",
      desc: "For commercial/client projects",
      icon: Building2,
    },
    {
      id: "Inquiring for Dealership/Distribution",
      title: "Dealership Partner",
      desc: "Business/distribution enquiry",
      icon: Store,
    },
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const response = await fetch(
        `https://webadmin.naturx.co.in/api/save_contact?name=${encodeURIComponent(
          formData.name,
        )}&phone=${encodeURIComponent(
          formData.mobile,
        )}&email=${encodeURIComponent(
          formData.email || "-",
        )}&msg=${encodeURIComponent(message)}`,
      );

      if (response.ok) {
        setShowToast(true);
        const whatsappMessage = `*New Free Site Visit Request*\n\n*Name:* ${formData.name}\n*Mobile:* ${formData.mobile}\n*Email:* ${formData.email || "N/A"}\n\n*Details:* ${message}`;

        setTimeout(() => {
          setShowToast(false);
          window.location.href = `https://wa.me/${businessPhoneNumber.replace(/\+/g, "")}?text=${encodeURIComponent(
            whatsappMessage,
          )}`;

          // Reset Form
          setFormData({
            name: "",
            mobile: "",
            pincode: "",
            email: "",
            whatsappUpdates: true,
            userType: "End User (Homeowner/Client)",
            selectedLocation: "",
          });
        }, 1200);
      } else {
        console.error("Failed to submit form data");
      }
    } catch (error) {
      console.error("Error submitting form data:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const fetchPincodeData = async () => {
      if (formData.pincode.length === 6) {
        setIsPincodeLoading(true);
        try {
          const pincodeResponse = await fetch(
            `https://api.postalpincode.in/pincode/${formData.pincode}`,
          );
          const pincodeResult = await pincodeResponse.json();
          if (pincodeResult[0]?.Status === "Success") {
            const locations = pincodeResult[0].PostOffice;
            setPincodeData(locations);
            if (locations.length > 0) {
              setFormData((prev) => ({
                ...prev,
                selectedLocation: locations[0].Name,
              }));
            }
          } else {
            setPincodeData([]);
          }
        } catch (error) {
          console.error("Error fetching pincode data:", error);
          setPincodeData([]);
        } finally {
          setIsPincodeLoading(false);
        }
      } else {
        setPincodeData([]);
      }
    };

    fetchPincodeData();
  }, [formData.pincode]);

  useEffect(() => {
    const selectedLocation = pincodeData.find(
      (location) => location.Name === formData.selectedLocation,
    );
    setMessage(
      `WhatsApp Updates: ${formData.whatsappUpdates ? "Yes" : "No"} | User Type: ${
        formData.userType
      } | Pincode: ${formData.pincode} | Location: ${
        selectedLocation ? selectedLocation.Name : "N/A"
      }, ${selectedLocation ? selectedLocation.District : ""}, ${
        selectedLocation ? selectedLocation.State : ""
      }`,
    );
  }, [
    pincodeData,
    formData.whatsappUpdates,
    formData.userType,
    formData.pincode,
    formData.selectedLocation,
  ]);

  return (
    <div className="relative bg-slate-50 text-slate-800 md:py-8 py-4 flex items-center justify-center overflow-hidden">
      {/* Background Soft Glows */}
      <div className="absolute top-1/4 -left-20 w-80 sm:w-96 h-80 sm:h-96 bg-amber-200/40 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute bottom-10 -right-20 w-80 sm:w-96 h-80 sm:h-96 bg-orange-200/40 rounded-full blur-3xl pointer-events-none" />

      {/* Success Notification Toast */}
      {showToast && (
        <div className="fixed top-6 right-6 z-50 flex items-center space-x-3 bg-emerald-600 text-white px-5 py-3.5 rounded-2xl shadow-xl font-semibold animate-bounce">
          <CheckCircle2 className="w-5 h-5 text-white" />
          <span>Appointment Request Received!</span>
        </div>
      )}

      <div className="max-w-7xl px-4 sm:px-6 lg:px-8 w-full grid grid-cols-1 lg:grid-cols-12 gap-4 lg:gap-8 items-stretch relative z-10">
        {/* Left Column: Visual Showcase (Col 12 on mobile/tablet, Col 6 on desktop) */}
        <div className="col-span-12 lg:col-span-6">
          <div className="w-full flex flex-col justify-between rounded-2xl overflow-hidden bg-white border border-slate-200/80 shadow-xl p-3 sm:p-4 transition-all duration-300 hover:shadow-2xl">
            <div className="w-full  bg-slate-100 rounded-2xl overflow-hidden min-h-[300px] sm:min-h-[400px] lg:min-h-[auto]">
              <img
                src={image1}
                alt="Book Free Site Visit"
                className="w-full h-auto max-h-[600px] object-contain rounded-xl"
              />
            </div>
          </div>
        </div>

        {/* Right Column: Form Container (Col 12 on mobile/tablet, Col 6 on desktop) */}
        <div className="col-span-12 lg:col-span-6 bg-white border border-slate-200/80 rounded-2xl p-4 shadow-xl shadow-slate-200/50">
          <div className="mb-4">
            <span className="inline-flex items-center space-x-2 px-3 py-1 rounded-full text-xs font-bold bg-amber-100 text-amber-800 border border-amber-200 mb-3">
              <Calendar className="w-3.5 h-3.5 text-amber-700" />
              <span>Complimentary Consultation</span>
            </span>
            <h1 className="text-xl lg:text-2xl font-semibold text-slate-900 tracking-tight">
              Book a Free Site Visit
            </h1>
            <p className="text-slate-500 text-xs sm:text-sm mt-2">
              Transform your space with NatureX Premium Textures. Complete the
              form below to get started.
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Input Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {/* Full Name */}
              <div className="space-y-1">
                <label
                  htmlFor="name"
                  className="text-xs font-semibold text-slate-700"
                >
                  Full Name <span className="text-amber-600">*</span>
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none text-slate-400">
                    <User className="w-4 h-4" />
                  </div>
                  <input
                    id="name"
                    type="text"
                    required
                    placeholder="John Doe"
                    className="w-full pl-10 pr-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-sm text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-amber-500/30 focus:border-amber-500 focus:bg-white transition-all"
                    value={formData.name}
                    onChange={(e) =>
                      setFormData({ ...formData, name: e.target.value })
                    }
                  />
                </div>
              </div>

              {/* Mobile Number */}
              <div className="space-y-1">
                <label
                  htmlFor="mobile"
                  className="text-xs font-semibold text-slate-700"
                >
                  Mobile Number <span className="text-amber-600">*</span>
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none text-slate-400">
                    <Phone className="w-4 h-4" />
                  </div>
                  <input
                    id="mobile"
                    type="tel"
                    required
                    placeholder="9998852256"
                    className="w-full pl-10 pr-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-sm text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-amber-500/30 focus:border-amber-500 focus:bg-white transition-all"
                    value={formData.mobile}
                    onChange={(e) => {
                      const val = e.target.value.replace(/\D/g, "");
                      if (val.length <= 10)
                        setFormData({ ...formData, mobile: val });
                    }}
                  />
                </div>
              </div>

              {/* Email Address */}
              <div className="space-y-1">
                <label
                  htmlFor="email"
                  className="text-xs font-semibold text-slate-700"
                >
                  Email Address
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none text-slate-400">
                    <Mail className="w-4 h-4" />
                  </div>
                  <input
                    id="email"
                    type="email"
                    placeholder="john@example.com"
                    className="w-full pl-10 pr-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-sm text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-amber-500/30 focus:border-amber-500 focus:bg-white transition-all"
                    value={formData.email}
                    onChange={(e) =>
                      setFormData({ ...formData, email: e.target.value })
                    }
                  />
                </div>
              </div>

              {/* Pincode */}
              <div className="space-y-1">
                <label
                  htmlFor="pincode"
                  className="text-xs font-semibold text-slate-700"
                >
                  Pincode <span className="text-amber-600">*</span>
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none text-slate-400">
                    <MapPin className="w-4 h-4" />
                  </div>
                  <input
                    id="pincode"
                    type="text"
                    required
                    placeholder="6-digit Pincode"
                    className="w-full pl-10 pr-10 py-3 bg-slate-50 border border-slate-200 rounded-xl text-sm text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-amber-500/30 focus:border-amber-500 focus:bg-white transition-all"
                    value={formData.pincode}
                    onChange={(e) => {
                      const val = e.target.value.replace(/\D/g, "");
                      if (val.length <= 6) {
                        setFormData({
                          ...formData,
                          pincode: val,
                          selectedLocation: "",
                        });
                      }
                    }}
                  />
                  {isPincodeLoading && (
                    <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
                      <Loader2 className="w-4 h-4 text-amber-600 animate-spin" />
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Location Selector */}
            {pincodeData.length > 0 && (
              <div className="space-y-1 animate-fadeIn">
                <label
                  htmlFor="location"
                  className="text-xs font-semibold text-slate-700"
                >
                  Select Area / Post Office{" "}
                  <span className="text-amber-600">*</span>
                </label>
                <select
                  id="location"
                  value={formData.selectedLocation}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      selectedLocation: e.target.value,
                    })
                  }
                  className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-amber-500/30 focus:border-amber-500 focus:bg-white transition-all"
                >
                  {pincodeData.map((loc, idx) => (
                    <option key={idx} value={loc.Name}>
                      {loc.Name} ({loc.District}, {loc.State})
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* Interactive User Type Selector */}
            <div className="space-y-1">
              <label className="text-xs font-semibold text-slate-700">
                I am inquiring as:
              </label>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                {userTypeOptions.map((option) => {
                  const Icon = option.icon;
                  const isSelected = formData.userType === option.id;
                  return (
                    <div
                      key={option.id}
                      onClick={() =>
                        setFormData({ ...formData, userType: option.id })
                      }
                      className={`cursor-pointer p-3.5 rounded-xl border text-left transition-all ${
                        isSelected
                          ? "bg-amber-50 border-amber-500 ring-2 ring-amber-500/20"
                          : "bg-slate-50 border-slate-200 hover:border-slate-300 hover:bg-slate-100/60"
                      }`}
                    >
                      <div className="flex items-center space-x-2">
                        <Icon
                          className={`w-4 h-4 ${
                            isSelected ? "text-amber-600" : "text-slate-500"
                          }`}
                        />
                        <span
                          className={`text-xs font-bold ${
                            isSelected ? "text-slate-900" : "text-slate-700"
                          }`}
                        >
                          {option.title}
                        </span>
                      </div>
                      <p className="text-[11px] text-slate-500 mt-1 leading-tight">
                        {option.desc}
                      </p>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* WhatsApp Updates Checkbox */}
            <div className="pt-1">
              <label className="inline-flex items-center cursor-pointer group">
                <input
                  type="checkbox"
                  checked={formData.whatsappUpdates}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      whatsappUpdates: e.target.checked,
                    })
                  }
                  className="sr-only"
                />
                <div
                  className={`w-5 h-5 rounded-md border flex items-center justify-center transition-all ${
                    formData.whatsappUpdates
                      ? "bg-emerald-600 border-emerald-600"
                      : "border-slate-300 bg-slate-50 group-hover:border-slate-400"
                  }`}
                >
                  {formData.whatsappUpdates && (
                    <MessageSquare className="w-3 h-3 text-white fill-current" />
                  )}
                </div>
                <span className="ml-3 text-xs font-medium text-slate-700 select-none">
                  Receive site visit updates via WhatsApp
                </span>
              </label>
            </div>

            {/* Disclaimer & Submit Button */}
            <div className="pt-4 border-t border-slate-200 flex flex-col sm:flex-row items-center justify-between gap-4">
              <p className="text-[11px] text-slate-500 text-center sm:text-left leading-relaxed">
                By submitting, you authorize NatureX Premium Textures to contact
                you regarding your request.
              </p>

              <button
                type="submit"
                disabled={isLoading}
                className="w-full sm:w-auto px-8 py-3.5 bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white font-bold text-sm rounded-xl transition-all shadow-lg shadow-amber-500/25 flex items-center justify-center space-x-2 disabled:opacity-50 shrink-0"
              >
                {isLoading ? (
                  <>
                    <span>Booking...</span>
                    <Loader2 className="w-4 h-4 animate-spin" />
                  </>
                ) : (
                  <>
                    <span>Confirm Booking</span>
                    <ArrowRight className="w-4 h-4" />
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};
